// ============================================
// AUTH TYPES - Unified authentication types
// ============================================

// Импортируем GlobalRole из permissions (единый источник правды)
import type { GlobalRole } from './permissions';
import type { PlanType } from '../lib/plans';

/**
 * User roles in the system (alias для обратной совместимости)
 * @deprecated используйте GlobalRole из './permissions'
 */
export type UserRole = GlobalRole;

/**
 * Onboarding status for new users
 */
export interface OnboardingStatus {
  completed: boolean;
  skipped: boolean;
  completedAt?: string;
  skippedAt?: string;
}

/**
 * User settings
 */
export interface UserSettings {
  theme: 'light' | 'dark';
  onboarding?: OnboardingStatus;
}

/**
 * Unified User interface - single source of truth
 */
export interface User {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  emailVerified: boolean;
  createdAt: string;
  settings?: UserSettings;
  avatarUrl?: string;
  plan?: PlanType; // Тарифный план пользователя
}

/**
 * Firestore user document structure
 */
export interface FirestoreUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  emailVerified: boolean;
  createdAt: string | Timestamp;
  settings?: UserSettings;
  avatarUrl?: string;
  isDeleted?: boolean;
  deletedAt?: string | Timestamp;
  plan?: PlanType; // Тарифный план пользователя
  projectCount?: number; // Количество обычных проектов
  demoProjectCount?: number; // Количество демо проектов
}

/**
 * Firebase Auth user data extension
 */
export interface FirebaseUserData {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
  providerData: { providerId: string }[];
}

// ============================================
// ERROR TYPES
// ============================================

/**
 * Firebase Auth error codes
 */
export enum AuthErrorCode {
  // Firebase Auth errors
  USER_NOT_FOUND = 'auth/user-not-found',
  WRONG_PASSWORD = 'auth/wrong-password',
  INVALID_EMAIL = 'auth/invalid-email',
  EMAIL_ALREADY_IN_USE = 'auth/email-already-in-use',
  WEAK_PASSWORD = 'auth/weak-password',
  TOO_MANY_REQUESTS = 'auth/too-many-requests',
  USER_DISABLED = 'auth/user-disabled',
  OPERATION_NOT_ALLOWED = 'auth/operation-not-allowed',
  REQUIRES_RECENT_LOGIN = 'auth/requires-recent-login',
  POPUP_CLOSED = 'auth/popup-closed-by-user',
  ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL = 'auth/account-exists-with-different-credential',
  NETWORK_REQUEST_FAILED = 'auth/network-request-failed',
  INVALID_CREDENTIAL = 'auth/invalid-credential',
  EMAIL_NOT_VERIFIED = 'auth/email-not-verified',
  EXPIRED_ACTION_CODE = 'auth/expired-action-code',
  INVALID_ACTION_CODE = 'auth/invalid-action-code',

  // Firestore errors
  PERMISSION_DENIED = 'permission-denied',
  NOT_FOUND = 'not-found',
  ALREADY_EXISTS = 'already-exists',

  // Custom errors
  NOT_AUTHENTICATED = 'custom/not-authenticated',
  USER_DELETED = 'custom/user-deleted',
  SESSION_EXPIRED = 'custom/session-expired',
  FIREBASE_NOT_CONFIGURED = 'custom/firebase-not-configured',
  POPUP_BLOCKED = 'custom/popup-blocked',
  POPUP_CANCELLED = 'custom/popup-cancelled',
  UNKNOWN_ERROR = 'custom/unknown-error'
}

/**
 * Authentication error
 */
export interface AuthError {
  code: AuthErrorCode | string;
  message: string;
  originalError?: Error;
}

/**
 * Authentication result
 */
export interface AuthResult<T = void> {
  success: boolean;
  data?: T;
  error?: AuthError;
}

// ============================================
// FORM TYPES
// ============================================

/**
 * Sign in credentials
 */
export interface SignInCredentials {
  email: string;
  password: string;
}

/**
 * Sign up credentials
 */
export interface SignUpCredentials {
  email: string;
  password: string;
  displayName: string;
}

/**
 * Password reset request
 */
export interface PasswordResetRequest {
  email: string;
}

/**
 * Change password request
 */
export interface ChangePasswordRequest {
  newPassword: string;
  currentPassword?: string; // For re-authentication
}

/**
 * Change email request
 */
export interface ChangeEmailRequest {
  newEmail: string;
  currentPassword?: string; // For re-authentication
}

/**
 * Role selection request
 */
export interface RoleSelectionRequest {
  role: Exclude<UserRole, 'pending'>;
}

/**
 * DEV mode login credentials
 */
export interface DevLoginCredentials {
  role: Exclude<UserRole, 'pending'>;
}

// ============================================
// UI STATE TYPES
// ============================================

/**
 * Auth UI state - stored in Zustand
 */
export interface AuthUIState {
  // Form states
  loginForm: {
    email: string;
    password: string;
    showPassword: boolean;
    errors: Partial<Record<keyof SignInCredentials, string>>;
  };
  registerForm: {
    email: string;
    password: string;
    displayName: string;
    showPassword: boolean;
    errors: Partial<Record<keyof SignUpCredentials, string>>;
  };

  // Page UI states
  isLoginTab: boolean;
  showForgotPassword: boolean;
  showEmailVerification: boolean;
  resendTimer: number;

  // DEV mode
  isDevMode: boolean;
}

/**
 * DEV auth state
 */
export interface DevAuthState {
  isDevUser: boolean;
  devUser: User | null;
}

// ============================================
// FIRESTORE TYPES
// ============================================

// Timestamp type from Firebase
export interface Timestamp {
  seconds: number;
  nanoseconds: number;
  toDate(): Date;
  toMillis(): number;
  isEqual(other: Timestamp): boolean;
}

// ============================================
// REGISTRATION FLOW TYPES
// ============================================

export type RegistrationStep = 
  | 'idle'
  | 'registering'
  | 'email-sent'
  | 'verifying'
  | 'verified'
  | 'role-selection'
  | 'error';

export type VerificationStatus = 
  | 'pending'
  | 'verified'
  | 'error';

export type RegistrationErrorType = 
  | 'network' 
  | 'invalid-code' 
  | 'expired' 
  | 'already-verified' 
  | 'generic';

export type RegistrationErrorAction = 
  | 'retry'
  | 'resend'
  | 'login'
  | 'skip';

export interface RegistrationFlowError {
  code: string;
  message: string;
  type: RegistrationErrorType;
  action?: RegistrationErrorAction;
}

export interface RegistrationFlowState {
  step: RegistrationStep;
  error: RegistrationFlowError | null;
  isLoading: boolean;
  userEmail: string | null;
  oobCode: string | null;
  verificationStatus: VerificationStatus;
}

export interface RegistrationResult {
  success: boolean;
  user?: User;
  error?: RegistrationFlowError;
}

export interface VerificationResult {
  success: boolean;
  isAuthenticated: boolean;
  error?: RegistrationFlowError;
}
